import { onBeforeUnmount, ref, watch, type Ref } from "vue";
import { accentFromPixels } from "../look";

/**
 * 🔴 **THE MARKS OF EVERY COMPANY ON SCREEN** (D77) — one implementation, for every module.
 *
 * 🔴 **IT LIVED IN THREE MODULES AND THEY WERE BYTE-IDENTICAL** (D94). `modules/admin`,
 * `modules/organisations` and `modules/management` each carried the same 95 lines, and the
 * organisations copy's own header said where it was going: *"the kit is where it goes the day a
 * third module draws a company."* PNK-109 is the day — the shell's switcher, a person's agency and a
 * production's supplier list all draw a company now.
 *
 * The sibling of `useFaces`, and the same shape for the same reasons: the loader is a PARAMETER,
 * because an MFE is its own build with its own API client and a kit that imported one module's
 * client would make every module depend on it.
 *
 * 🔴 **WHY A GRID CANNOT PUT A URL IN AN `<img>`.** A logo is served to a SESSION — the endpoint
 * checks the caller's scope before it hands over bytes, and a browser sends no bearer for an
 * `<img src>`. So the bytes come through the API client and become object URLs for as long as the
 * screen is open.
 *
 * 🔴 **IT DOES NOT KNOW A LOGO FROM A HERO.** The subject carries an id and a content hash, and the
 * caller's `load` decides which endpoint that means. One composable serves the mark on a row and the
 * background behind a company's name, because the fetching problem is identical and the day they
 * diverge is the day one of them silently stops revoking its object URLs.
 */
export interface MarkSubject {
  /** The company's public id (D39). */
  id: string;
  /** The content hash of the image, or null/undefined when there is none to fetch. */
  sha256: string | null | undefined;
}

export interface MarksView {
  /** Object URL per company id — reactive, so a list re-renders as marks arrive. */
  urls: Ref<ReadonlyMap<string, string>>;
  /** How many are still in flight. Reaches a screen only if a caller wants it. */
  pending: Ref<number>;
  /**
   * 🔴 **THE ACCENT EACH MARK GIVES** (D164 rule 5), per company id — `accentFromPixels` over the fetched
   * bytes, handed to `companyLook` as the fallback BELOW `brandColours`. Absent until read, and for good when
   * the pixels cannot be read. Never written back to the row.
   */
  accents: Ref<ReadonlyMap<string, string>>;
}

export interface MarksDeps {
  /** Fetches the bytes for one company. The ONLY line that ever differed between the three copies. */
  load: (organisationId: string) => Promise<Blob>;
  createUrl?: (blob: Blob) => string;
  revokeUrl?: (url: string) => void;
  /** How many at once. Fifty rows firing fifty requests is a queue the browser makes anyway. */
  concurrency?: number;
  /** Reads a fetched mark's RGBA pixels from its object URL. The default draws it on an `OffscreenCanvas`. */
  sample?: (url: string) => Promise<ArrayLike<number> | null>;
  /** Accent per logo CONTENT hash. The default is shared by every `useMarks` in this bundle. */
  accentCache?: Map<string, string | null>;
}

/** The edge a mark is drawn at to be read — enough pixels to pool an ink, few enough to cost nothing. */
const SAMPLE_EDGE = 48;

/**
 * 🔴 **EVERY MARK THIS BUNDLE HAS READ, BY ITS CONTENT HASH** — so the switcher and a person card drawing the
 * same agency read its logo once, and a replaced logo (a new hash) is read again.
 */
const sharedAccents = new Map<string, string | null>();

/**
 * The mark's pixels, drawn on an `OffscreenCanvas`. The URL is an object URL this session minted from bytes
 * it fetched itself — same-origin, so the canvas is not tainted and `getImageData` answers. An SVG has no
 * intrinsic size to trust, so every mark is drawn into the same square. No canvas (a test DOM, an old
 * browser) is not an error: the mark is drawn, and the look falls through to the name tint.
 */
async function sampleWithCanvas(url: string): Promise<ArrayLike<number> | null> {
  if (typeof OffscreenCanvas === "undefined" || typeof Image === "undefined") return null;
  const image = new Image();
  image.src = url;
  await image.decode();
  const canvas = new OffscreenCanvas(SAMPLE_EDGE, SAMPLE_EDGE);
  const context = canvas.getContext("2d");
  if (context === null) return null;
  context.drawImage(image, 0, 0, SAMPLE_EDGE, SAMPLE_EDGE);
  return context.getImageData(0, 0, SAMPLE_EDGE, SAMPLE_EDGE).data;
}

const defaultCreateUrl = (blob: Blob): string => (typeof URL.createObjectURL === "function" ? URL.createObjectURL(blob) : "");
const defaultRevokeUrl = (url: string): void => {
  if (typeof URL.revokeObjectURL === "function") URL.revokeObjectURL(url);
};

/** The same image, by CONTENT — a new object URL is made only when the bytes changed. */
const keyOf = (subject: MarkSubject): string => `${subject.id}:${subject.sha256 ?? ""}`;
const hasMark = (subject: MarkSubject): boolean => typeof subject.sha256 === "string" && subject.sha256 !== "";

export function useMarks(subjects: () => MarkSubject[], deps: MarksDeps): MarksView {
  const createUrl = deps.createUrl ?? defaultCreateUrl;
  const revokeUrl = deps.revokeUrl ?? defaultRevokeUrl;
  const concurrency = deps.concurrency ?? 6;
  const sample = deps.sample ?? sampleWithCanvas;
  const cache = deps.accentCache ?? sharedAccents;

  const urls = ref<ReadonlyMap<string, string>>(new Map());
  const accents = ref<ReadonlyMap<string, string>>(new Map());
  const pending = ref(0);
  // 🔴 A refusal is an ANSWER. A company whose mark this session may not read answers once, and
  // asking again on every re-render would be a retry loop against a settled reply.
  const seen = new Set<string>();
  const held = new Map<string, string>();
  let alive = true;

  /** Their accent: from the cache when this logo has been read, else read once now. A failed read is null. */
  async function accentOf(sha256: string, url: string): Promise<string | null> {
    if (cache.has(sha256)) return cache.get(sha256) ?? null;
    let accent: string | null = null;
    try {
      const pixels = await sample(url);
      accent = pixels === null ? null : accentFromPixels(pixels);
    } catch {
      // Unreadable pixels leave the mark on screen and the look on the name tint.
    }
    cache.set(sha256, accent);
    return accent;
  }

  async function fetchOne(subject: MarkSubject): Promise<void> {
    try {
      const blob = await deps.load(subject.id);
      if (!alive) return;
      const previous = held.get(subject.id);
      if (previous !== undefined) revokeUrl(previous);
      const url = createUrl(blob);
      held.set(subject.id, url);
      urls.value = new Map(held);
      const accent = await accentOf(subject.sha256 ?? "", url);
      if (!alive) return;
      const next = new Map(accents.value);
      if (accent === null) next.delete(subject.id);
      else next.set(subject.id, accent);
      accents.value = next;
    } catch {
      // Nothing to draw, and the monogram is already the answer on screen.
    } finally {
      pending.value -= 1;
    }
  }

  async function run(queue: MarkSubject[]): Promise<void> {
    const workers = Array.from({ length: Math.min(concurrency, queue.length) }, async () => {
      for (let next = queue.shift(); next !== undefined && alive; next = queue.shift()) await fetchOne(next);
    });
    await Promise.all(workers);
  }

  watch(
    () => subjects().map(keyOf).join(","),
    () => {
      const queue = subjects().filter((subject) => hasMark(subject) && !seen.has(keyOf(subject)));
      for (const subject of queue) seen.add(keyOf(subject));
      if (queue.length === 0) return;
      pending.value += queue.length;
      void run(queue);
    },
    { immediate: true },
  );

  onBeforeUnmount(() => {
    alive = false;
    for (const url of held.values()) revokeUrl(url);
    held.clear();
  });

  return { urls, pending, accents };
}
